/**
 * Defense utilities for the FAITRACKER form-tracking library (factors.js).
 *
 * The external script crashes with `TypeError: Cannot read properties of null
 * (reading 'split')` when form inputs lack id/name attributes. These utilities
 * provide:
 *   1. A Proxy on window.FAITRACKER_FORM_FILLS that rejects bad keys and
 *      guarantees string returns.
 *   2. A global error handler that suppresses the specific 'split' TypeError
 *      from factors.js.
 *
 * See Sentry DATON-WEBAPP-1B.
 */

import * as Sentry from '@sentry/angular';

/**
 * Records a Sentry breadcrumb when Freshsales trackEvent fails.
 * The error is suppressed — this provides observability without
 * opening new Sentry issues for expected third-party failures.
 */
const logFreshsalesTrackFailure = (eventName: string, error: unknown): void => {
  Sentry.addBreadcrumb({
    category: 'freshsales',
    message: 'Freshsales trackEvent failed',
    level: 'warning',
    data: {
      eventName,
      error: error instanceof Error ? error.message : String(error),
    },
  });
};

/**
 * Checks if a property key is unsafe for FAITRACKER storage.
 * Unsafe keys are non-strings, empty strings, or the literal strings 'null'/'undefined'.
 */
export function isUnsafeKey(prop: string | symbol): boolean {
  return typeof prop !== 'string' || prop === '' || prop === 'null' || prop === 'undefined';
}

/**
 * Creates a Proxy that safely stores form field values for FAITRACKER.
 *
 * The proxy:
 * - Silently drops writes with unsafe keys (empty, 'null', 'undefined', Symbol)
 * - Returns empty string for reads of unsafe keys or null/undefined values
 * - Guarantees all reads return a value safe to call `.split()` on
 */
export function createFaitrackerProxy(): Record<string, any> {
  return new Proxy<Record<string, any>>({}, {
    set(target, prop, value): boolean {
      if (isUnsafeKey(prop)) {
        return true;
      }
      target[prop as string] = value;
      return true;
    },
    get(target, prop): any {
      if (isUnsafeKey(prop)) {
        return '';
      }
      const value = target[prop as string];
      return value == null ? '' : value;
    }
  });
}

/**
 * Safely calls `window.freshsales.trackEvent` when the Freshsales library is
 * loaded and the method is available.
 *
 * Guards against the TypeError that occurs when the Freshsales script is
 * blocked, slow-loading, or missing (e.g. ad-blockers, network issues).
 * The Freshsales init snippet sets `window.freshsales = []` before the actual
 * analytics.js loads, so the guard checks `typeof trackEvent === 'function'`
 * rather than relying on the object being truthy alone.
 *
 * Fixes Sentry DATON-WEBAPP-H (ClickUp #86d3n0jzn): TypeError in the
 * edit-warehouse route when users selected a warehouse before freshsales loaded.
 *
 * @param eventName The Freshsales event name to track (e.g. 'dataWarehouseSelected').
 * @param data Optional key-value payload to attach to the event.
 */
export function trackFreshsalesEvent(eventName: string, data?: Record<string, unknown>): void {
  try {
    const freshsales = window['freshsales'];
    if (freshsales && typeof freshsales.trackEvent === 'function') {
      freshsales.trackEvent(eventName, data);
    }
  } catch (error) {
    // Freshsales not available or not fully initialized — suppress the throw
    // but record a Sentry breadcrumb for observability.
    // Fixes DATON-WEBAPP-H: window.freshsales can be torn down or throw
    // between the guard check and the actual call.
    logFreshsalesTrackFailure(eventName, error);
  }
}

/**
 * Installs a global error handler that suppresses the specific 'split' TypeError
 * from the FAITRACKER factors.js script.
 *
 * Only suppresses errors that:
 * - Originate from a file containing 'factors.js' in the path
 * - Have a message containing "reading 'split'" (the specific TypeError we're guarding)
 *
 * @param target The window object to attach the handler to
 * @returns A cleanup function that removes the handler
 */
export function installFactorsJsErrorSuppressor(target: Window): () => void {
  const handler = (event: ErrorEvent): void => {
    if (
      event.filename?.includes('factors.js') &&
      event.message?.includes("reading 'split'")
    ) {
      event.preventDefault();
    }
  };

  target.addEventListener('error', handler);

  return () => target.removeEventListener('error', handler);
}
